import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_FILTERS,
  PAGE_SIZE,
  SORTS,
  activeFilterCount,
  type BrowseFilters,
  type Facets,
  type Listing,
  type ListingsPage,
  dealLine,
  dismiss,
  displayTitle,
  fetchListings,
  filtersFromSearch,
  metaLine,
  remaining,
  save,
  staleNote,
  summaryLine,
  unsave,
} from "../lib/browsePage";
import { formatPacificDate } from "../lib/format";
import { CARD_GRID } from "../lib/cardGrid";

/**
 * The merch feed, ported from `server/app.py`.
 *
 * The logic lives in `lib/browsePage.ts` and the filtering lives on the server;
 * what is here is rendering, the filter controls, and the paging state machine.
 *
 * PAGING IS APPEND, NOT REPLACE. Reaching the bottom fetches the next offset
 * and concatenates onto what is already shown. Changing any
 * filter resets to offset 0 and discards what was on screen, because the
 * accumulated pages were drawn from a different query and mixing them would
 * show listings that do not match the filters the sidebar is displaying.
 */

function Card({
  item,
  busy,
  onSave,
  onUnsave,
  onDismiss,
}: {
  item: Listing;
  busy: boolean;
  onSave: (item: Listing) => void;
  onUnsave: (item: Listing) => void;
  onDismiss: (item: Listing) => void;
}) {
  const meta = metaLine(item);
  const deal = dealLine(item);
  const found = formatPacificDate(item.first_seen);
  const stale = staleNote(item);

  return (
    <li className="rounded-md border border-neutral-800 bg-neutral-900 p-3 space-y-2">
      {item.image_url ? (
        // `rel` is not optional on a target=_blank link to a marketplace:
        // without `noopener` the opened page gets a handle on this one.
        <a href={item.url || undefined} target="_blank" rel="noopener noreferrer">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full rounded aspect-square object-cover bg-neutral-800"
          />
        </a>
      ) : null}

      <div>
        <p className="font-semibold text-sm">
          {item.artist}
          {item.is_new ? (
            <span className="ml-2 rounded bg-green-900 px-1.5 py-0.5 text-[10px] font-medium text-green-300 align-middle">
              NEW
            </span>
          ) : null}
        </p>
        {/* `capitalize` rather than a JS title-case pass: a naive title-case
            mangles "USA" to "Usa". CSS only changes rendering,
            so the underlying value stays what the scraper recorded. */}
        <p className="text-sm text-neutral-300 capitalize">{displayTitle(item)}</p>
      </div>

      {deal ? <p className="text-xs text-green-400">{deal}</p> : null}
      {meta ? <p className="text-xs text-neutral-500">{meta}</p> : null}
      {found ? <p className="text-xs text-neutral-500">Found {found}</p> : null}
      {stale ? <p className="text-xs text-amber-500">{stale}</p> : null}

      <div className="flex gap-2 pt-1">
        <button
          disabled={busy}
          onClick={() => (item.saved ? onUnsave(item) : onSave(item))}
          className={
            item.saved
              ? "flex-1 rounded bg-neutral-100 px-2 py-1.5 text-sm text-neutral-900 hover:bg-white"
              : "flex-1 rounded border border-neutral-700 px-2 py-1.5 text-sm text-neutral-300 hover:border-neutral-500 hover:text-white disabled:opacity-40"
          }
        >
          {item.saved ? "★ Saved" : "☆ Save"}
        </button>
        <button
          disabled={busy}
          onClick={() => onDismiss(item)}
          className="flex-1 rounded border border-neutral-700 px-2 py-1.5 text-sm text-neutral-400 hover:border-neutral-500 hover:text-white disabled:opacity-40"
        >
          Dismiss
        </button>
      </div>
    </li>
  );
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  if (options.length === 0) return null;
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-neutral-400">
        {label}
        {selected.length > 0 ? ` (${selected.length})` : ""}
      </span>
      {/* A native multiple-select rather than a custom widget: it is keyboard
          accessible for free, and 160 artists in a bespoke dropdown is a
          scrolling problem nobody asked us to solve. */}
      <select
        multiple
        value={selected}
        onChange={(e) =>
          onChange(Array.from(e.target.selectedOptions, (option) => option.value))
        }
        className="h-24 min-w-40 rounded border border-neutral-700 bg-neutral-950 px-1 py-1 text-neutral-200"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Filters({
  facets,
  filters,
  onChange,
  onReset,
}: {
  facets: Facets;
  filters: BrowseFilters;
  onChange: (next: BrowseFilters) => void;
  onReset: () => void;
}) {
  // Open on a wide screen, closed on a phone. Five `h-24` multi-selects wrap
  // into a wall you have to scroll past before reaching any merch, which is
  // most of what made mobile scrolling bad. Read once at mount rather than
  // tracked: a phone does not become a desktop mid-session, and a resize
  // listener that snaps the panel open under your thumb is worse than stale.
  // `typeof window.matchMedia === "function"`, not `typeof window !== "undefined"`.
  // The environment that lacks matchMedia is not one that lacks window -- jsdom
  // has the second and not the first -- so the original guard passed and then
  // threw. Defaulting to closed when it cannot be asked is the safe side: a
  // collapsed panel is one click from open, an exception renders nothing.
  const [open] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(min-width: 640px)").matches,
  );
  const active = activeFilterCount(filters);

  return (
    <details open={open} className="rounded-md border border-neutral-800 bg-neutral-900">
      <summary className="cursor-pointer px-3 py-2 text-sm text-neutral-300 hover:text-white">
        Filters
        {active > 0 ? (
          <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-900">
            {active}
          </span>
        ) : null}
        {filters.sort !== "newest" ? (
          <span className="ml-2 text-xs text-neutral-500">
            {SORTS.find((s) => s.value === filters.sort)?.label}
          </span>
        ) : null}
      </summary>
      <div className="flex flex-wrap items-start gap-4 border-t border-neutral-800 p-3">
      <MultiSelect
        label="Artist"
        options={facets.artists}
        selected={filters.artists}
        onChange={(artists) => onChange({ ...filters, artists })}
      />
      <MultiSelect
        label="Source"
        options={facets.platforms}
        selected={filters.platforms}
        onChange={(platforms) => onChange({ ...filters, platforms })}
      />
      <MultiSelect
        label="Type"
        options={facets.types}
        selected={filters.types}
        onChange={(types) => onChange({ ...filters, types })}
      />
      <MultiSelect
        label="Size"
        options={facets.sizes}
        selected={filters.sizes}
        onChange={(sizes) => onChange({ ...filters, sizes })}
      />
      <MultiSelect
        label="Color"
        options={facets.colors}
        selected={filters.colors}
        onChange={(colors) => onChange({ ...filters, colors })}
      />

      <div className="flex flex-col gap-2 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-neutral-400">Sort by</span>
          <select
            value={filters.sort}
            onChange={(e) =>
              onChange({ ...filters, sort: e.target.value as BrowseFilters["sort"] })
            }
            className="rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-200"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-neutral-400">Max price</span>
          <input
            type="number"
            min={0}
            placeholder={facets.price_max === null ? "" : String(Math.round(facets.price_max))}
            value={filters.priceMax ?? ""}
            onChange={(e) =>
              onChange({
                ...filters,
                // An empty box is "no bound", not zero. Sending 0 would hide
                // every priced listing and look like the page had broken.
                priceMax: e.target.value === "" ? null : Number(e.target.value),
              })
            }
            className="w-28 rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-200"
          />
        </label>

        <label className="flex items-center gap-2 text-neutral-400">
          <input
            type="checkbox"
            checked={filters.dealsOnly}
            onChange={(e) => onChange({ ...filters, dealsOnly: e.target.checked })}
            className="accent-neutral-100"
          />
          Deals only
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-neutral-400">Found on</span>
          <input
            type="date"
            value={filters.newOn ?? ""}
            onChange={(e) => onChange({ ...filters, newOn: e.target.value || null })}
            className="rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-200"
          />
        </label>

        <button onClick={onReset} className="text-left text-neutral-500 underline hover:text-neutral-300">
          Reset filters
        </button>
      </div>
      </div>
    </details>
  );
}

export default function Browse() {
  const [filters, setFilters] = useState<BrowseFilters>(() =>
    filtersFromSearch(typeof window === "undefined" ? "" : window.location.search),
  );
  const [page, setPage] = useState<ListingsPage | null>(null);
  const [items, setItems] = useState<Listing[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  // IntersectionObserver can deliver more than one callback before React has
  // committed `loadingMore=true`. This synchronous guard prevents two requests
  // for the same offset during that window.
  const loadingMoreRef = useRef(false);
  // A page requested for an old filter must never append into the new query.
  // Incrementing this token also lets the new query start paging immediately
  // without waiting for an obsolete request to settle.
  const pagingGenerationRef = useRef(0);
  // ONE IN-FLIGHT ACTION PER CARD. Without this, two actions on the same card
  // race and the loser wins: two saves where the second fails leave the button
  // reading "Save" while the server holds it saved, and a save-then-dismiss
  // that both fail put the card back marked "★ Saved" because the dismiss
  // rollback restores the item object captured mid-save. Both were reproduced.
  // Serialising per card removes the class rather than reconciling each case.
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(() => new Set());
  // The REF is the guard; the state is only what renders it. `disabled` on the
  // button cannot serialise anything on its own -- it needs a commit to take
  // effect, so two clicks dispatched before React renders both get through and
  // both fire real requests. Reproduced. The ref is written synchronously in
  // `act`, so the second call sees it in the same task.
  const busyKeysRef = useRef<Set<string>>(new Set());
  // A mirror of `items` for the one thing that needs to read the list outside a
  // state updater: where a dismissed card sat, so a failed dismiss can put it
  // back in place rather than at the end.
  const itemsRef = useRef<Listing[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Keep the shareable email filter honest when it is changed or reset in the
  // UI. Other browse filters remain intentionally session-local.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (filters.newOn === null) url.searchParams.delete("new_on");
    else url.searchParams.set("new_on", filters.newOn);
    window.history.replaceState(window.history.state, "", url);
  }, [filters.newOn]);

  // Refetch from the top whenever the query changes. `filters` is the only
  // dependency on purpose: paging must NOT retrigger this, or every "Load more"
  // would immediately be undone by a reset to offset 0.
  useEffect(() => {
    let cancelled = false;
    pagingGenerationRef.current += 1;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setError(null);
    setPage(null);
    fetchListings(filters, 0)
      .then((first) => {
        // A filter changed while this was in flight. Dropping the stale answer
        // is the difference between the grid matching the sidebar and not.
        if (cancelled) return;
        setPage(first);
        setItems(first.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const loadMore = useCallback(() => {
    if (!page || loadingMoreRef.current || items.length >= page.total) return;
    const generation = pagingGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setActionError(null);
    fetchListings(filters, items.length, PAGE_SIZE)
      .then((next) => {
        if (generation !== pagingGenerationRef.current) return;
        setPage(next);
        // Concatenate onto the existing items. De-duped by
        // key because a write between two pages shifts the offsets underneath
        // us and the same listing can arrive twice.
        setItems((current) => {
          const seen = new Set(current.map((i) => i.key));
          return [...current, ...next.items.filter((i) => !seen.has(i.key))];
        });
      })
      .catch((err: unknown) => {
        if (generation === pagingGenerationRef.current) {
          setActionError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (generation === pagingGenerationRef.current) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      });
  }, [filters, items.length, page]);

  // Re-observe after each appended page. If the viewport is tall enough that
  // the sentinel is still visible, the next page should load too rather than
  // leaving the user at the bottom with more results silently available.
  useEffect(() => {
    const target = loadMoreSentinelRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, loadingMore]);

  /**
   * Save, un-save and dismiss: one optimistic patch, one rollback on failure.
   *
   * BOTH updaters are FUNCTIONAL, and the rollback especially. It used to
   * snapshot the whole array (`const previous = items`) and restore it wholesale
   * on failure, which quietly undid every OTHER action that had landed in the
   * meantime: save A, dismiss B while A is in flight, A fails, and B comes back
   * from the dead with an error message about A. Reverting only the card that
   * failed is the fix, and it also lets this drop its `items` dependency, so the
   * callbacks stop being rebuilt on every list change.
   */
  const act = useCallback(
    (
      key: string,
      run: () => Promise<void>,
      optimistic: (current: Listing[]) => Listing[],
      rollback: (current: Listing[]) => Listing[],
    ) => {
      // Synchronous, before anything else: this is what actually serialises.
      if (busyKeysRef.current.has(key)) return;
      busyKeysRef.current.add(key);
      setBusyKeys(new Set(busyKeysRef.current));

      setActionError(null);
      setItems(optimistic);
      // Called directly, not deferred through a microtask: the request should
      // start now, and every caller (`save`/`unsave`/`dismiss` in
      // `lib/browsePage.ts`) is an async function, so an internal throw becomes
      // a rejection rather than a synchronous one that would skip `.finally`
      // and strand the key. A future non-async caller would need its own guard.
      run()
        .catch((err: unknown) => {
          setItems(rollback);
          setActionError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          busyKeysRef.current.delete(key);
          setBusyKeys(new Set(busyKeysRef.current));
        });
    },
    [],
  );

  /** Flip one card's saved flag, leaving every other card alone. */
  const setSaved = (key: string, saved: boolean) => (current: Listing[]) =>
    current.map((i) => (i.key === key ? { ...i, saved } : i));

  const onSave = useCallback(
    (item: Listing) =>
      act(item.key, () => save(item), setSaved(item.key, true), setSaved(item.key, false)),
    [act],
  );

  const onUnsave = useCallback(
    (item: Listing) =>
      act(item.key, () => unsave(item.key), setSaved(item.key, false), setSaved(item.key, true)),
    [act],
  );

  const onDismiss = useCallback(
    (item: Listing) => {
      // Dismissing removes the card immediately. The total is not decremented:
      // it comes from the server and will be right on the next fetch, and
      // guessing at it here is how the count and the list start disagreeing.
      //
      // The index is read from a ref rather than computed inside the updater,
      // because a state updater must stay free of side effects -- React may
      // invoke it more than once. On failure the card goes back where it was,
      // clamped, since the list may have shifted underneath in the meantime.
      const index = itemsRef.current.findIndex((i) => i.key === item.key);
      act(
        item.key,
        () => dismiss(item),
        (current) => current.filter((i) => i.key !== item.key),
        (current) => {
          if (current.some((i) => i.key === item.key)) return current;
          const at = index < 0 ? current.length : Math.min(index, current.length);
          return [...current.slice(0, at), item, ...current.slice(at)];
        },
      );
    },
    [act],
  );

  if (error) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Browse</h1>
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        <button
          onClick={() => setFilters({ ...filters })}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 hover:bg-white"
        >
          Try again
        </button>
      </section>
    );
  }

  if (page === null) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Browse</h1>
        <p className="text-sm text-neutral-400">Loading…</p>
      </section>
    );
  }

  const left = remaining(items.length, page.total);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-4">
        <h1 className="text-xl font-semibold">Browse</h1>
        <p className="text-sm text-neutral-500">
          {page.saved_count} saved · {page.dismissed_count} dismissed
        </p>
      </div>

      <Filters
        facets={page.facets}
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(DEFAULT_FILTERS)}
      />

      <p className="text-sm text-neutral-400">{summaryLine(items.length, page.total)}</p>

      {actionError ? (
        <div className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {actionError}
        </div>
      ) : null}

      {page.total === 0 ? (
        <p className="text-sm text-neutral-400">No listings match your filters.</p>
      ) : null}

      <ul className={CARD_GRID}>
        {items.map((item) => (
          <Card
            key={item.key}
            item={item}
            busy={busyKeys.has(item.key)}
            onSave={onSave}
            onUnsave={onUnsave}
            onDismiss={onDismiss}
          />
        ))}
      </ul>

      {left > 0 ? (
        <div
          ref={loadMoreSentinelRef}
          role="status"
          aria-live="polite"
          className="py-2 text-center text-sm text-neutral-500"
        >
          {loadingMore ? "Loading more…" : `${left} more listings`}
          {typeof IntersectionObserver === "undefined" ? (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="ml-2 underline hover:text-neutral-300 disabled:opacity-50"
            >
              Load more
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
